/**
 * Settings › Integrations — Meetings (TRUS-692). Org admins choose whether
 * hosts may create guest links (off by default), how long a link can work
 * (≤ 168 h) and how many guests a meeting can take (≤ 25). Everyone else sees
 * the settings read-only (PUT is org admin only). Shown when the org has
 * meetings (GET /meetings/config); the guest-link controls need the
 * `messaging_meeting_guest_links` capability too (`guest_links_available`).
 *
 * GET / PUT /api/messaging/meetings/settings (cbo.settings.meetings).
 */

import React, { useEffect, useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useAppTranslation } from '@/contexts/TranslationContext';
import useActiveOrgId from '@/hooks/useActiveOrgId';
import {
  DEFAULT_MEETINGS_ORG_SETTINGS,
  GUEST_LINK_MAX_HOURS_LIMIT,
  MAX_GUESTS_LIMIT,
  type MeetingsOrgSettings,
} from '@/services/meetingsApi';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { useIsOrgAdmin } from '../channelActions';
import { useMeetingsApi, useMeetingsConfig } from './useMeetings';

export const GUEST_LINKS_CAPABILITY = 'messaging_meeting_guest_links';

export function MeetingsSettingsPanel() {
  const { t } = useAppTranslation();
  const api = useMeetingsApi();
  const orgId = useActiveOrgId();
  const meetingsConfig = useMeetingsConfig();
  const isOrgAdmin = useIsOrgAdmin();
  const queryClient = useQueryClient();
  const ids = { hours: useId(), guests: useId(), toggle: useId() };
  const key = ['meetings-org-settings', orgId];

  const q = useQuery({
    queryKey: key,
    queryFn: () => api.getOrgSettings(),
    enabled: meetingsConfig.enabled && api.kind === 'staff',
    retry: false,
    staleTime: 30_000,
  });
  const [draft, setDraft] = useState<MeetingsOrgSettings>(DEFAULT_MEETINGS_ORG_SETTINGS);
  useEffect(() => {
    if (q.data) setDraft(q.data.settings);
  }, [q.data]);

  const save = useMutation({
    mutationFn: (s: MeetingsOrgSettings) => api.saveOrgSettings(s),
    onSuccess: (res) => {
      queryClient.setQueryData(key, res);
      toast.success(t('messaging.meetings.settingsSaved', 'Meeting settings saved'));
    },
    onError: (err: unknown) =>
      toast.error(t('messaging.meetings.settingsSaveFailed', 'Could not save the meeting settings'), {
        description: (err as Error)?.message,
      }),
  });

  if (!meetingsConfig.enabled) return null;
  const guestLinksAvailable = !!q.data?.guest_links_available;
  const canManage = isOrgAdmin && guestLinksAvailable;
  const hoursOk = Number.isInteger(draft.guest_link_max_hours) && draft.guest_link_max_hours >= 1 && draft.guest_link_max_hours <= GUEST_LINK_MAX_HOURS_LIMIT;
  const guestsOk = Number.isInteger(draft.max_guests) && draft.max_guests >= 1 && draft.max_guests <= MAX_GUESTS_LIMIT;
  const dirty =
    !!q.data &&
    (draft.guest_links_enabled !== q.data.settings.guest_links_enabled ||
      draft.guest_link_max_hours !== q.data.settings.guest_link_max_hours ||
      draft.max_guests !== q.data.settings.max_guests);

  return (
    <section className="flex flex-col gap-4" data-testid="meetings-settings">
      <div>
        <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
          <CalendarClock className="h-5 w-5" aria-hidden="true" />
          {t('messaging.meetings.settingsTitle', 'Meetings')}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t(
            'messaging.meetings.settingsHint',
            'Guest links let people without a WeaveIn account join a scheduled meeting. The host lets each guest in from the call.',
          )}
        </p>
      </div>

      {q.isLoading ? (
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      ) : q.isError ? (
        <p className="text-sm text-muted-foreground" data-testid="meetings-settings-error">
          {t('messaging.meetings.settingsUnavailable', 'Meeting settings are not available right now.')}
        </p>
      ) : (
        <>
          {!guestLinksAvailable && (
            <p className="flex items-center gap-1.5 text-sm text-muted-foreground" data-testid="meetings-guest-capability-off">
              <Lock className="h-4 w-4" aria-hidden="true" />
              {t('messaging.meetings.guestCapabilityOff', 'Guest links are not part of this organization’s plan.')}
            </p>
          )}
          {guestLinksAvailable && !isOrgAdmin && (
            <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Lock className="h-4 w-4" aria-hidden="true" />
              {t('messaging.meetings.settingsReadOnly', 'Only an organization admin can change these.')}
            </p>
          )}
          <div className="flex items-center justify-between gap-4">
            <Label htmlFor={ids.toggle} className="flex flex-col gap-0.5">
              <span>{t('messaging.meetings.guestLinksToggle', 'Allow guest links')}</span>
              <span className="text-xs font-normal text-muted-foreground">
                {t('messaging.meetings.guestLinksToggleHint', 'Off by default. Turning it off stops every guest link working.')}
              </span>
            </Label>
            <Switch
              id={ids.toggle}
              checked={draft.guest_links_enabled}
              disabled={!canManage}
              onCheckedChange={(v: boolean) => setDraft((d) => ({ ...d, guest_links_enabled: v }))}
              data-testid="meetings-guest-links-toggle"
            />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={ids.hours}>{t('messaging.meetings.maxLinkHours', 'A link works for at most (hours)')}</Label>
              <Input
                id={ids.hours}
                type="number"
                min={1}
                max={GUEST_LINK_MAX_HOURS_LIMIT}
                value={Number.isFinite(draft.guest_link_max_hours) ? draft.guest_link_max_hours : ''}
                disabled={!canManage || !draft.guest_links_enabled}
                onChange={(e) => setDraft((d) => ({ ...d, guest_link_max_hours: Number(e.target.value) }))}
                aria-invalid={!hoursOk}
                data-testid="meetings-max-hours"
              />
              {!hoursOk && (
                <p className="text-xs text-red-600 dark:text-red-400" role="alert">
                  {t('messaging.meetings.maxLinkHoursError', 'Between 1 and {{max}} hours.', { max: GUEST_LINK_MAX_HOURS_LIMIT })}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={ids.guests}>{t('messaging.meetings.maxGuests', 'Guests per meeting')}</Label>
              <Input
                id={ids.guests}
                type="number"
                min={1}
                max={MAX_GUESTS_LIMIT}
                value={Number.isFinite(draft.max_guests) ? draft.max_guests : ''}
                disabled={!canManage || !draft.guest_links_enabled}
                onChange={(e) => setDraft((d) => ({ ...d, max_guests: Number(e.target.value) }))}
                aria-invalid={!guestsOk}
                data-testid="meetings-max-guests"
              />
              {!guestsOk && (
                <p className="text-xs text-red-600 dark:text-red-400" role="alert">
                  {t('messaging.meetings.maxGuestsError', 'Between 1 and {{max}} guests.', { max: MAX_GUESTS_LIMIT })}
                </p>
              )}
            </div>
          </div>
          {canManage && (
            <div>
              <Button
                type="button"
                disabled={!dirty || !hoursOk || !guestsOk || save.isPending}
                onClick={() => save.mutate(draft)}
                data-testid="meetings-settings-save"
              >
                {save.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                {t('messaging.meetings.saveChanges', 'Save changes')}
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

export default MeetingsSettingsPanel;
